import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { CreateTreatmentCreateMessageParams } from './dtos/createMessage.dto';
import type { CreateTreatmentCreateTopicParams } from './dtos/createTopic.dto';
import type { GetAllTreatmentCreatesAvailableParams } from './dtos/getAllAvailable.dto';
import type { GetAllTreatmentCreatesByCollaboratorParams } from './dtos/getAllByCollaborator.dto';
import type { ImportTreatmentCreateTopicsParams } from './dtos/importTopics.dto';
import type { RemoveTreatmentCreateTopicParams } from './dtos/remove.dto';
import type { RemoveAllTreatmentCreateTopicsParams } from './dtos/removeAll.dto';
import type { ReorderTreatmentCreateTopicsParams } from './dtos/reorder.dto';
import type { UpdateTreatmentCreateTopicParams } from './dtos/update.dto';
import type { UpdateTreatmentCreateMessageParams } from './dtos/updateMessage.dto';
import type { TreatmentCreatesRepository } from './repository';
import type { CreateMessageBody } from './types/createMessage.type';
import type { CreateTopicBody } from './types/createTopic.type';
import type {
  GetAllTreatmentCreatesAvailableOutput,
  GetAllTreatmentCreatesAvailableResponse,
} from './types/getAllAvailable.type';
import type {
  GetAllTreatmentCreatesByCollaboratorOutput,
  GetAllTreatmentCreatesByCollaboratorResponse,
} from './types/getAllByCollaborator.type';
import type {
  GetTreatmentCreateByIdDataResponse,
  GetTreatmentCreateByIdOutput,
} from './types/getById.type';
import type {
  ImportTopicsOutput,
  ImportTopicsResponse,
} from './types/importTopics.type';
import type { ReorderTopicsBody } from './types/reorderTopics.type';
import type { UpdateMessageBody } from './types/updateMessage.type';
import type { UpdateTopicBody } from './types/updateTopic.type';

class TreatmentCreatesService implements TreatmentCreatesRepository {
  public async getAllByCollaborator(
    params?: GetAllTreatmentCreatesByCollaboratorParams,
  ): Promise<GetAllTreatmentCreatesByCollaboratorOutput> {
    const { data } = await backendHttpClient.get<
      GetAllTreatmentCreatesByCollaboratorResponse,
      GetAllTreatmentCreatesByCollaboratorParams
    >({
      path: 'treatment-creates/collaborator',
      params,
    });

    const output: GetAllTreatmentCreatesByCollaboratorOutput = {
      list: data.list.map(item => ({
        amount: item.amount,
        startedAt: item.started_at,
        expiredAt: item.expired_at,
        id: item.id,
        priority: item.priority,
        status: item.status,
        title: item.title,
      })),
      pagination: {
        page: params?.page || 1,
        totalPages: data.totalPages,
      },
    };
    return output;
  }

  public async getAllAvailable(
    params?: GetAllTreatmentCreatesAvailableParams,
  ): Promise<GetAllTreatmentCreatesAvailableOutput> {
    const { data } = await backendHttpClient.get<
      GetAllTreatmentCreatesAvailableResponse,
      GetAllTreatmentCreatesAvailableParams
    >({
      path: 'treatment-creates/available',
      params,
    });

    const output: GetAllTreatmentCreatesAvailableOutput = {
      list: data.list.map(item => ({
        amount: item.amount,
        createdAt: item.created_at,
        id: item.id,
        priority: item.priority,
        status: item.status,
        title: item.title,
      })),
      pagination: {
        page: params?.page || 1,
        totalPages: data.totalPages,
      },
    };
    return output;
  }

  public async getById(id: string): Promise<GetTreatmentCreateByIdOutput> {
    const { data: treatment } =
      await backendHttpClient.get<GetTreatmentCreateByIdDataResponse>({
        path: `treatment-creates/${id}`,
      });
    const output: GetTreatmentCreateByIdOutput = {
      treatment: {
        amount: treatment.amount,
        collaborator: treatment.collaborator,
        expiredAt: treatment.expired_at,
        id: treatment.id,
        priority: treatment.priority,
        status: treatment.status,
        title: treatment.title,
        topics: treatment.topics,
        messages: treatment.messages.map(item => ({
          createdAt: item.created_at,
          id: item.id,
          message: item.message,
          publishedAt: item.published_at,
          treatmentId: item.treatment_create_id,
          type: item.type,
        })),
      },
    };
    return output;
  }

  public async assignCollaborator(id: string): Promise<void> {
    await backendHttpClient.get<void>({
      path: `treatment-creates/${id}/collaborator`,
    });
  }

  public async unassignCollaborator(id: string): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `treatment-creates/${id}/collaborator`,
    });
  }

  public async removeAllTopics(
    params: RemoveAllTreatmentCreateTopicsParams,
  ): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `treatment-creates/${params.treatmentCreateId}/topics`,
    });
  }

  public async removeTopic(
    params: RemoveTreatmentCreateTopicParams,
  ): Promise<void> {
    await backendHttpClient.delete<void>({
      path: `treatment-creates/${params.treatmentCreateId}/topics/${params.id}`,
    });
  }

  public async updateTopic(
    params: UpdateTreatmentCreateTopicParams,
  ): Promise<void> {
    await backendHttpClient.put<void, UpdateTopicBody>({
      path: `treatment-creates/${params.treatmentCreateId}/topics/${params.id}`,
      body: {
        content: params.content,
        // position: params.position,
        title: params.title,
      },
    });
  }

  public async createMessage(
    params: CreateTreatmentCreateMessageParams,
  ): Promise<void> {
    await backendHttpClient.post<void, CreateMessageBody>({
      path: `treatment-creates/${params.treatmentCreateId}/messages`,
      body: {
        message: params.message,
      },
    });
  }

  public async updateMessage(
    params: UpdateTreatmentCreateMessageParams,
  ): Promise<void> {
    await backendHttpClient.put<void, UpdateMessageBody>({
      path: `treatment-creates/${params.treatmentCreateId}/messages/${params.treatmentCreateMessageId}`,
      body: {
        message: params.message,
      },
    });
  }

  public async createTopic(
    params: CreateTreatmentCreateTopicParams,
  ): Promise<void> {
    await backendHttpClient.post<void, CreateTopicBody>({
      path: `treatment-creates/${params.treatmentCreateId}/topics`,
      body: {
        content: params.content,
        position: params.position,
        title: params.title,
      },
    });
  }

  public async importTopics(
    params: ImportTreatmentCreateTopicsParams,
  ): Promise<ImportTopicsOutput> {
    const formData = new FormData();

    formData.append('file', params.file);

    const { data } = await backendHttpClient.post<
      ImportTopicsResponse,
      FormData
    >({
      path: `treatment-creates/${params.treatmentCreateId}/topics/import`,
      body: formData,
    });

    return {
      topicsCreated: data.topicsCreated,
      warnings: data.warnings,
    };
  }

  public async reorderTopics(
    params: ReorderTreatmentCreateTopicsParams,
  ): Promise<void> {
    await backendHttpClient.patch<void, ReorderTopicsBody>({
      path: `treatment-creates/${params.treatmentCreateId}/topics`,
      body: {
        ids: params.ids,
      },
    });
  }

  public async markReview(id: string): Promise<void> {
    await backendHttpClient.get<void>({
      path: `treatment-creates/${id}/review`,
    });
  }
}

export const treatmentCreatesService = new TreatmentCreatesService();
